import { motion } from "framer-motion";
import { Syringe, FlaskConical, Dumbbell, Stethoscope, Check } from "lucide-react";

const painPoints = [
  "Está acima do peso (10, 20, 30kg ou mais)",
  "Cansada e sem disposição para nada",
  "Acorda com dificuldade e com muito sono",
  "Sedentária — não acha tempo na agenda pra se exercitar",
  "Termina o dia com a barriga estufada e má digestão",
  "Engordou muito nos últimos 2–5 anos e não consegue emagrecer sozinha",
  "Sente uma fome exagerada pouco tempo após as refeições",
  "Sonolência excessiva após o almoço ou durante o dia",
];

const included = [
  {
    icon: Syringe,
    text: "Injetáveis específicos para o tratamento da obesidade com reposição de vitaminas e minerais.",
  },
  {
    icon: FlaskConical,
    text: "Fórmulas de farmácia para tratamento da obesidade.",
  },
  {
    icon: Dumbbell,
    text: "Avaliação e prescrição com Personal.",
  },
  {
    icon: Stethoscope,
    text: "Consultas + acompanhamento médico + dose de Tirzepatida conforme prescrição.",
  },
];

const phases = [
  {
    n: "01",
    title: "Detox e reposição de vitaminas",
    desc: "Aceleramos seu metabolismo e o processo de detox, ajudando o corpo a eliminar toxinas através da alimentação e preparando seu intestino para a fase seguinte, voltada a emagrecimento, disposição e saúde.",
  },
  {
    n: "02",
    title: "EmagreSER",
    desc: "Iniciamos os emagrecedores, mantendo a reposição de vitaminas e minerais e potencializando o emagrecimento com medicações personalizadas de acordo com seus exames.",
  },
  {
    n: "03",
    title: "Sculpt Pro",
    desc: "Iniciamos o processo de definição corporal com um protocolo para secar: estratégias personalizadas de nutrição e suplementação voltadas à redução de gordura e ao aumento de massa muscular.",
  },
];

const Protocol = () => {
  return (
    <section
      id="metodo-slim-pro"
      className="relative overflow-hidden bg-[#FDFBF7] py-20 md:py-32"
      data-testid="protocol-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-16 max-w-3xl text-center"
        >
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            Não é apenas uma consulta
          </p>
          <h2
            className="font-serif text-3xl leading-tight text-[#3E2723] sm:text-4xl lg:text-5xl gold-line-center"
            data-testid="protocol-title"
          >
            Método <em className="italic text-[#8B6F1F]">Slim Pro</em> — 3 meses
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#4E342E]/80 md:text-lg">
            Um programa de acompanhamento médico completo e individualizado. Após
            sua consulta e a elaboração do protocolo personalizado, caso seja
            indicado pela Dra. Mariana, você terá um cuidado integral por 3
            meses — muito além de uma consulta pontual.
          </p>
        </motion.div>

        {/* Pain points + photo */}
        <div className="mb-20 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-5"
          >
            <div className="absolute -inset-3 rounded-[2.5rem] border border-[#D4AF37]/30" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_metodo-mariana/artifacts/01utl1lr_IMG_0541.JPG"
              alt="Dra. Mariana Alves"
              className="relative h-[520px] w-full rounded-[2rem] object-cover"
              data-testid="protocol-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <h3 className="font-serif text-2xl text-[#3E2723] md:text-3xl">
              Esse método é pra você, mulher, se você está:
            </h3>
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {painPoints.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-2xl border border-[#EFEBE1] bg-white px-5 py-4"
                  data-testid={`protocol-pain-${i}`}
                >
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#D4AF37]">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-[#4E342E]">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* 3 Phases timeline */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 text-center font-serif text-2xl text-[#3E2723] md:text-3xl"
          >
            As 3 fases da sua transformação
          </motion.h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {phases.map((phase, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="relative rounded-3xl border border-[#EFEBE1] bg-white p-8 transition-all hover:-translate-y-2 hover:border-[#D4AF37]/30 hover:shadow-[0_20px_40px_rgba(212,175,55,0.12)]"
                data-testid={`protocol-phase-${i}`}
              >
                <span className="font-serif text-5xl text-[#D4AF37]/30">
                  {phase.n}
                </span>
                <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#795548]">
                  Fase {phase.n.replace("0", "")}
                </p>
                <h4 className="mt-2 font-serif text-xl text-[#3E2723] md:text-2xl">
                  {phase.title}
                </h4>
                <p className="mt-4 text-sm leading-relaxed text-[#4E342E]/80">
                  {phase.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* What's included */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-[2.5rem] bg-[#3E2723] p-8 md:p-14"
        >
          <p className="mb-3 text-center text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            Cuidado integral
          </p>
          <h3 className="text-center font-serif text-2xl text-[#FDFBF7] md:text-3xl">
            O que o programa inclui
          </h3>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {included.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm"
                  data-testid={`protocol-included-${i}`}
                >
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#3E2723]">
                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                  </span>
                  <p className="text-sm leading-relaxed text-[#FDFBF7]/90 md:text-base">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-center font-serif text-lg italic text-[#D4AF37]">
            “O seu futuro é magro e comigo ele começa agora.”
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Protocol;
